import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Users,
  UserPlus,
  Mail,
  Phone,
  Calendar,
  Shield,
  Check,
  X,
  Edit3,
  Trash2,
  Search,
  MoreVertical,
  AlertCircle,
  Building2,
  Send,
  Info,
} from "lucide-react";
import MainHeader from "../MainHeader";
import useIAM from "../hooks/useIam";
import InviteModal from "../modal/iam/InviteModal";
import EditMeModal from "../modal/iam/EditMeModal";
import EditEnterpriseModal from "../modal/iam/EditEnterpriseModal";
import DropdownMenu from "../modal/iam/DropdownMenu";
import { showConfirm } from "../modal/confirmModal";

/**
 * 구글 스타일 톤의 심플 UI
 * - 상단 고정 헤더 + Card + 얇은 구분선
 * - 권한 기반: 소유자만 초대/추방/승인탭 접근 가능
 * - 상태 배너/로딩/오류/권한메시지 처리
 * - 초기 로딩시 모든 데이터 한번에 가져오기
 */
const IAMDashboard = () => {
  const {
    updateSelf,
    inviteMember,
    banMember,
    approveRoster,
    refuseRoster,
    cancelInviteEmail,
    updateEnterpriseInfo,
    fetchAllIAMData,
  } = useIAM();

  const [activeTab, setActiveTab] = useState("members");
  const [isMaster, setIsMaster] = useState(false);
  const [members, setMembers] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [invites, setInvites] = useState([]);
  const [enterprise, setEnterprise] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    user_name: "",
    email: "",
    hp: "",
  });
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({
    display_name: "",
    description: "",
  });

  // 드롭다운 버튼 refs
  const dropdownButtonRefs = useRef({});

  const currentUser = useMemo(() => members.find((m) => m.is_me), [members]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("ko-KR");
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredMembers = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    return members.filter((m) => {
      const name = (m.user_name || "").toLowerCase();
      const email = (m.email || "").toLowerCase();
      const hp = (m.hp || "").toLowerCase();
      return name.includes(q) || email.includes(q) || hp.includes(q);
    });
  }, [members, searchQuery]);

  const loadAllData = async () => {
    setInitialLoading(true);
    setErrorMsg("");
    const res = await fetchAllIAMData();

    if (!res.ok) {
      setErrorMsg("데이터를 불러오는데 실패했습니다.");
    } else {
      setMembers(res.members);
      setIsMaster(res.isMaster);
      setApprovals(res.approvals);
      setInvites(res.invites);
      setEnterprise(res.enterprise);

      // 현재 사용자 정보로 폼 초기화
      const me = res.members.find((m) => m.is_me);
      if (me) {
        setEditForm({
          user_name: me.user_name || "",
          email: me.email || "",
          hp: me.hp || "",
        });
      }

      // 회사 정보 폼 초기화
      if (res.enterprise) {
        setEnterpriseForm({
          display_name: res.enterprise.display_name || "",
          description: res.enterprise.description || "",
        });
      }
    }
    setInitialLoading(false);
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // 메시지 자동 숨기기
  useEffect(() => {
    if (infoMsg) {
      const timer = setTimeout(() => setInfoMsg(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [infoMsg]);

  useEffect(() => {
    if (errorMsg) {
      const timer = setTimeout(() => setErrorMsg(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  const handleInvite = async () => {
    setErrorMsg("");
    setInfoMsg("");
    if (!inviteEmail) {
      setErrorMsg("이메일을 입력하세요.");
      return;
    }
    const res = await inviteMember(inviteEmail);
    if (res.permissionDenied) {
      setInfoMsg("초대 권한이 없습니다. (소유자 전용)");
      return;
    }
    if (!res.ok) {
      setErrorMsg("초대에 실패했습니다.");
      return;
    }
    setShowInviteModal(false);
    setInviteEmail("");
    setInfoMsg("초대 메일을 발송했습니다.");
    await loadAllData();
  };

  const handleBan = async (member) => {
    if (!isMaster) return;
    if (member.is_me) return;

    const confirmed = await showConfirm({
      title: "멤버 추방",
      message: `${member.user_name} 님을 추방하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
      confirmText: "추방",
      cancelText: "취소",
      variant: "error",
    });

    if (!confirmed) return;

    const res = await banMember(member.id);
    if (res.permissionDenied) {
      setInfoMsg("추방 권한이 없습니다. (소유자 전용)");
      return;
    }
    if (!res.ok) {
      setErrorMsg("멤버 추방에 실패했습니다.");
      return;
    }
    setInfoMsg("멤버를 추방했습니다.");
    setDropdownOpen(null);
    await loadAllData();
  };

  const handleCancelInvite = async (invite) => {
    const confirmed = await showConfirm({
      title: "초대 취소",
      message: `${invite.invite_email}에 대한 초대를 취소하시겠습니까?`,
      confirmText: "취소",
      cancelText: "유지",
      variant: "warning",
    });

    if (!confirmed) return;

    const res = await cancelInviteEmail(invite.id);
    if (res.permissionDenied) {
      setInfoMsg("초대 취소 권한이 없습니다. (소유자 전용)");
      return;
    }
    if (!res.ok) {
      setErrorMsg("초대 취소에 실패했습니다.");
      return;
    }
    setInfoMsg("초대를 취소했습니다.");
    await loadAllData();
  };

  const handleSaveMe = async () => {
    setErrorMsg("");
    setInfoMsg("");
    const ok = await updateSelf(editForm);
    if (!ok) {
      setErrorMsg("수정에 실패했습니다.");
      return;
    }
    setShowEditModal(false);
    setInfoMsg("내 정보를 저장했어요.");
    await loadAllData();
  };

  const handleSaveEnterprise = async () => {
    setErrorMsg("");
    setInfoMsg("");
    const res = await updateEnterpriseInfo(enterpriseForm);
    if (res.permissionDenied) {
      setInfoMsg("회사 정보 수정 권한이 없습니다. (소유자 전용)");
      return;
    }
    if (!res.ok) {
      setErrorMsg("회사 정보 수정에 실패했습니다.");
      return;
    }
    setShowEnterpriseModal(false);
    setInfoMsg("회사 정보를 저장했습니다.");
    await loadAllData();
  };

  const handleApprove = async (item) => {
    const res = await approveRoster(item.id);
    if (res.permissionDenied) {
      setInfoMsg("승인 권한이 없습니다. (소유자 전용)");
      return;
    }
    if (res.alreadyWorkspace) {
      setInfoMsg("이미 다른 워크스페이스에서 승인된 사용자입니다.");
      await loadAllData();
      return;
    }
    if (!res.ok) {
      setErrorMsg("승인 처리에 실패했습니다.");
      return;
    }
    setInfoMsg("승인 완료되었습니다.");
    await loadAllData();
  };

  const handleRefuse = async (item) => {
    const res = await refuseRoster(item.id);
    if (res.permissionDenied) {
      setInfoMsg("거절 권한이 없습니다. (소유자 전용)");
      return;
    }
    if (!res.ok) {
      setErrorMsg("거절 처리에 실패했습니다.");
      return;
    }
    setInfoMsg("거절 완료되었습니다.");
    await loadAllData();
  };

  const Banner = ({ type = "info", message }) => {
    if (!message) return null;
    const cls =
      type === "error"
        ? "bg-red-50 text-red-800 border-red-200"
        : "bg-blue-50 text-blue-800 border-blue-200";
    const Icon = type === "error" ? AlertCircle : Info;
    return (
      <div
        className={`mb-4 border ${cls} rounded-md px-3 py-2 text-sm flex items-center gap-2`}
      >
        <Icon className="w-4 h-4" />
        <span>{message}</span>
      </div>
    );
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );

  const EmptyState = ({ icon: Icon, label }) => (
    <div className="p-12 text-center">
      <Icon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
      <p className="text-gray-500">{label}</p>
    </div>
  );

  if (initialLoading) {
    return (
      <>
        <MainHeader />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-xl font-medium text-gray-900">IAM 관리</h1>
              {isMaster && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  멤버 초대
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <Banner type="info" message={infoMsg} />
          <Banner type="error" message={errorMsg} />

          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("members")}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "members"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  멤버 목록
                  <span className="bg-gray-100 text-gray-900 px-2 py-0.5 rounded-full text-xs">
                    {members.length}
                  </span>
                </div>
              </button>

              {isMaster && (
                <>
                  <button
                    onClick={() => setActiveTab("approvals")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === "approvals"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      승인 대기
                      <span className="bg-gray-100 text-gray-900 px-2 py-0.5 rounded-full text-xs">
                        {approvals.length}
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => setActiveTab("invites")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === "invites"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      초대한 메일
                      <span className="bg-gray-100 text-gray-900 px-2 py-0.5 rounded-full text-xs">
                        {invites.length}
                      </span>
                    </div>
                  </button>
                </>
              )}

              <button
                onClick={() => setActiveTab("enterprise")}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "enterprise"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  회사 정보
                </div>
              </button>
            </nav>
          </div>

          {/* 멤버 목록 탭 */}
          {activeTab === "members" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="이름, 이메일 또는 전화번호로 검색"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        사용자
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        이메일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        전화번호
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        권한
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        가입일
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td
                          className="px-6 py-6 text-center text-sm text-gray-500"
                          colSpan={6}
                        >
                          멤버가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map((member) => (
                        <tr key={member.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                <span className="text-gray-600 font-medium">
                                  {(member.user_name || "?")[0]}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {member.user_name}
                                  {member.is_me && (
                                    <span className="ml-2 text-xs text-gray-500">
                                      (나)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Mail className="w-4 h-4 mr-2 text-gray-400" />
                              {member.email || "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              {member.hp || "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {member.grade === "owner" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <Shield className="w-3 h-3 mr-1" />
                                소유자
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                참여자
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              {formatDate(member.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="relative inline-block text-left">
                              <button
                                ref={(el) =>
                                  (dropdownButtonRefs.current[member.id] = el)
                                }
                                onClick={() =>
                                  setDropdownOpen(
                                    dropdownOpen === member.id
                                      ? null
                                      : member.id
                                  )
                                }
                                className="text-gray-400 hover:text-gray-600 p-2"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>

                              <DropdownMenu
                                isOpen={dropdownOpen === member.id}
                                onClose={() => setDropdownOpen(null)}
                                buttonRef={
                                  dropdownButtonRefs.current[member.id]
                                    ? {
                                        current:
                                          dropdownButtonRefs.current[member.id],
                                      }
                                    : null
                                }
                              >
                                {member.is_me ? (
                                  <button
                                    onClick={() => {
                                      setEditForm({
                                        user_name: currentUser?.user_name || "",
                                        email: currentUser?.email || "",
                                        hp: currentUser?.hp || "",
                                      });
                                      setShowEditModal(true);
                                      setDropdownOpen(null);
                                    }}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                  >
                                    <Edit3 className="w-4 h-4 mr-2" />내 정보
                                    수정
                                  </button>
                                ) : (
                                  isMaster &&
                                  member.grade !== "master" && (
                                    <button
                                      onClick={() => handleBan(member)}
                                      className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      멤버 추방
                                    </button>
                                  )
                                )}
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 승인 대기 탭 */}
          {activeTab === "approvals" && isMaster && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {approvals.length === 0 ? (
                <EmptyState
                  icon={Shield}
                  label="승인 대기 중인 멤버가 없습니다."
                />
              ) : (
                <div className="p-6 space-y-4">
                  {approvals.map((ap) => (
                    <div
                      key={ap.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <UserPlus className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-gray-900">
                                  {ap.email}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {ap.hp || "-"}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(ap.created_at)} 신청
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApprove(ap)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                          >
                            <Check className="w-4 h-4" />
                            승인
                          </button>
                          <button
                            onClick={() => handleRefuse(ap)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                          >
                            <X className="w-4 h-4" />
                            거절
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 초대한 메일 탭 */}
          {activeTab === "invites" && isMaster && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {invites.length === 0 ? (
                <EmptyState icon={Send} label="초대한 메일이 없습니다." />
              ) : (
                <div className="p-6 space-y-4">
                  {invites.map((invite) => (
                    <div
                      key={invite.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Mail className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {invite.invite_email}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDateTime(invite.created_at)} 초대 발송
                              </p>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleCancelInvite(invite)}
                          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
                        >
                          <X className="w-4 h-4" />
                          초대 취소
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 회사 정보 탭 */}
          {activeTab === "enterprise" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {enterprise ? (
                <div className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {enterprise.display_name || "회사명 미등록"}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          플랜: {enterprise.usage_plan || "Free"}
                        </p>
                      </div>
                    </div>
                    {isMaster && (
                      <button
                        onClick={() => {
                          setEnterpriseForm({
                            display_name: enterprise.display_name || "",
                            description: enterprise.description || "",
                          });
                          setShowEnterpriseModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
                      >
                        <Edit3 className="w-4 h-4" />
                        수정
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        회사 설명
                      </label>
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-900">
                          {enterprise.description || "설명이 없습니다."}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          생성일
                        </label>
                        <p className="text-sm text-gray-900">
                          {formatDate(enterprise.created_at)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Workspace Serial ID
                        </label>
                        <p className="text-sm text-gray-900">
                          {enterprise.owner_id}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={Building2}
                  label="회사 정보를 불러올 수 없습니다."
                />
              )}
            </div>
          )}
        </div>

        <InviteModal
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          onSubmit={handleInvite}
        />

        <EditMeModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          form={editForm}
          onChange={setEditForm}
          onSave={handleSaveMe}
        />

        <EditEnterpriseModal
          open={showEnterpriseModal}
          onClose={() => setShowEnterpriseModal(false)}
          form={enterpriseForm}
          onChange={setEnterpriseForm}
          onSave={handleSaveEnterprise}
        />
      </div>
    </>
  );
};

export default IAMDashboard;
